import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import ProductCard from "../components/ProductCard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizProducts } from "../store/actowiz/actowizSlice";

const BRAND_OPTIONS = [
  { key: "tesco", label: "Tesco" },
  { key: "asda", label: "Asda" },
  { key: "morrison", label: "Morrisons" },
  { key: "sainsbury", label: "Sainsbury’s" },
];

export default function BaseProducts({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const items = useAppSelector((s) => s.actowiz.unfilteredProducts);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);
  const allCatalog = useAppSelector((s) => s.actowiz.allCatalog);

  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [brandsOpen, setBrandsOpen] = useState(true);
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState(() => new Set());
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(() => new Set());
  const totalProducts =
    typeof allCatalog?.totalCount === "number" ? allCatalog.totalCount : items.length;

  const isLoading = Object.values(statusByPlatform || {}).some((s) => s === "loading");
  const error = Object.values(errorByPlatform || {}).find(Boolean) || "";

  useEffect(() => {
    const start = (page - 1) * pageSize;
    dispatch(
      fetchActowizProducts({
        platform_list: selectedBrands.size ? Array.from(selectedBrands) : undefined,
        category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
        sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
        start,
        offset: pageSize,
        mode: "replace",
        force: true,
      })
    );
  }, [dispatch, page, pageSize, selectedBrands, selectedCategories, selectedSubcategories]);

  const hasPrev = page > 1;
  const hasNext = items.length === pageSize;

  const getRequestKey = (targetPage) => {
    const brandKey = Array.from(selectedBrands).join(",").trim().toLowerCase();
    const catKey = Array.from(selectedCategories).join(",").trim().toLowerCase();
    const subKey = Array.from(selectedSubcategories).join(",").trim().toLowerCase();
    return `${brandKey || "all"}:${encodeURIComponent(catKey)}:${encodeURIComponent(subKey)}:${(targetPage - 1) * pageSize}:${pageSize}`;
  };

  const categoryOptions = useMemo(() => {
    const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
    return cats
      .map((c) => String(c?.category || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [allCatalog]);

  const subcategoryOptions = useMemo(() => {
    const cats = Array.isArray(allCatalog?.categories) ? allCatalog.categories : [];
    const subs = new Set();
    cats.forEach((c) => {
      const cat = String(c?.category || "").trim();
      if (selectedCategories.size > 0 && !selectedCategories.has(cat)) return;
      (Array.isArray(c?.subcategories) ? c.subcategories : []).forEach((sc) => {
        const v = String(sc || "").trim();
        if (v) subs.add(v);
      });
    });
    return Array.from(subs).sort((a, b) => a.localeCompare(b));
  }, [allCatalog, selectedCategories]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-[90rem] flex-1 px-3 py-4 sm:px-4 sm:py-6 md:px-6">
        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-5 md:flex-row md:items-end md:justify-between">
          <div>
            <BackButton className="mb-3" />
            <h1 className="text-[24px] font-bold text-[#24303A] md:text-[28px]">
              Products
            </h1>
            <p className="mt-1 text-[14px] font-normal text-[#6E7B86] sm:text-[16px]">
              Browse featured products.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 text-[14px] sm:text-[16px]">
            <span className="rounded-full border border-[#E6EAF0] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#6E7B86] sm:text-[14px]">
              Total products: {totalProducts}
            </span>
            <button
              type="button"
              disabled={!hasPrev || isLoading}
              onClick={() =>
                setPage((v) => {
                  const nextPage = Math.max(1, v - 1);
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full border-2 shadow-sm border-[#E6EAF0] bg-[#FFFFFF1A] px-3 py-1.5 font-bold text-[#6E7B86] disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-[#6E7B86] text-[14px] font-normal sm:text-[16px]">Page {page}</span>
            <button
              type="button"
              disabled={!hasNext || isLoading}
              onClick={() =>
                setPage((v) => {
                  const nextPage = v + 1;
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
           className="rounded-full bg-[#7352C7] px-3 py-1.5 font-semibold text-white disabled:opacity-50 shadow-sm"
            >
              Next
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          <aside className="hidden w-64 rounded-2xl border border-[#E6EAF0] bg-white p-4 text-[16px] shadow-sm md:block sticky top-24 h-[calc(100vh-90px)]">
            <div className="mb-4 flex items-center justify-between border-b-2 border-[#E6EAF0] pb-4">
              <h2 className="text-[20px] font-medium text-[#24303A]">Filters</h2>
              <button
                type="button"
                onClick={() => {
                  setPage(1);
                  setSelectedBrands(new Set());
                  setSelectedCategories(new Set());
                  setSelectedSubcategories(new Set());
                }}
                className="rounded-full bg-[#7352C7] px-3 py-2 text-[14px] font-semibold text-white shadow-sm"
              >
                Reset
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-2 h-[calc(100%-56px)]">
              <div className="bg-white border-b-2 border-[#E6EAF0] pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-1 py-2 text-left"
                  onClick={() => setBrandsOpen((v) => !v)}
                >
                  <span className="text-[16px] font-medium text-[#24303A]">Brands</span>
                  <span className="text-[#6E7B86]">{brandsOpen ? "▴" : "▾"}</span>
                </button>
                {brandsOpen && (
                  <div className="px-3 pb-3 space-y-2 text-[16px] text-[#6E7B86]">
                    {BRAND_OPTIONS.map((b) => (
                      <label key={b.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedBrands.has(b.key)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedBrands((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(b.key);
                              else next.delete(b.key);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{b.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border-b-2 border-[#E6EAF0] pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                  onClick={() => setCategoryOpen((v) => !v)}
                >
                  <span className="text-[16px] font-medium text-[#24303A]">Category</span>
                  <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
                </button>
                {categoryOpen && (
                  <div className="px-3 pb-3 space-y-2 text-[16px] text-[#6E7B86]">
                    {categoryOptions.length === 0 && (
                      <div className="text-[12px] text-slate-400">No categories</div>
                    )}
                    {categoryOptions.map((c) => (
                      <label key={c} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(c)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedCategories((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(c);
                              else next.delete(c);
                              return next;
                            });
                            setSelectedSubcategories(new Set());
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                  onClick={() => setSubCategoryOpen((v) => !v)}
                >
                  <span className="text-[16px] font-medium text-[#24303A]">Sub Category</span>
                  <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
                </button>
                {subCategoryOpen && (
                  <div className="px-3 pb-3 space-y-2 text-[16px] text-[#6E7B86]">
                    {subcategoryOptions.length === 0 && (
                      <div className="text-[12px] text-slate-400">No sub categories</div>
                    )}
                    {subcategoryOptions.map((sc) => (
                      <label key={sc} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.has(sc)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedSubcategories((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(sc);
                              else next.delete(sc);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{sc}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <section className="flex-1 space-y-6">
            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-semibold text-[#24303A] shadow-sm"
              >
                <span>Filters</span>
                <span className="text-[#7352C7]">
                  {selectedBrands.size + selectedCategories.size + selectedSubcategories.size > 0
                    ? `(${selectedBrands.size + selectedCategories.size + selectedSubcategories.size})`
                    : ""}
                </span>
              </button>
            </div>

            {isLoading && items.length === 0 && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-slate-500">Loading…</span>
              </div>
            )}

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[16px] text-red-700 whitespace-pre-line">
                {error}
              </div>
            )}

            {!error && !isLoading && items.length === 0 && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-slate-500">No products found.</span>
              </div>
            )}

            {!error && items.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
                {items.map((product, idx) => (
                  <ProductCard
                    key={`${product.pid || product.sku}-${idx}`}
                    accent={["orange", "blue", "green", "purple"][idx % 4]}
                    product={product}
                    showComparison={false}
                    onClick={() =>
                      navigate(
                        `/product-store/${encodeURIComponent(product.platform || "")}/${encodeURIComponent(
                          product.pid || product.sku
                        )}`,
                        { state: { product } }
                      )
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[80] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close filters"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm border-l border-[#E6EAF0] bg-white p-4 shadow-xl">
            <div className="mb-4 flex items-center justify-between border-b border-[#E6EAF0] pb-3">
              <h2 className="text-[18px] font-semibold text-[#24303A]">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] text-slate-600"
                aria-label="Close filters panel"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pb-24">
              <div className="border-b border-[#E6EAF0] pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-1 py-2 text-left"
                  onClick={() => setBrandsOpen((v) => !v)}
                >
                  <span className="text-[14px] font-medium text-[#24303A]">Brands</span>
                  <span className="text-[#6E7B86]">{brandsOpen ? "▴" : "▾"}</span>
                </button>
                {brandsOpen && (
                  <div className="space-y-2 px-2 pb-2 text-[14px] text-[#6E7B86]">
                    {BRAND_OPTIONS.map((b) => (
                      <label key={b.key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedBrands.has(b.key)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedBrands((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(b.key);
                              else next.delete(b.key);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{b.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-b border-[#E6EAF0] pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-1 py-2 text-left"
                  onClick={() => setCategoryOpen((v) => !v)}
                >
                  <span className="text-[14px] font-medium text-[#24303A]">Category</span>
                  <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
                </button>
                {categoryOpen && (
                  <div className="space-y-2 px-2 pb-2 text-[14px] text-[#6E7B86]">
                    {categoryOptions.length === 0 && (
                      <div className="text-[12px] text-slate-400">No categories</div>
                    )}
                    {categoryOptions.map((c) => (
                      <label key={c} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(c)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedCategories((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(c);
                              else next.delete(c);
                              return next;
                            });
                            setSelectedSubcategories(new Set());
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="pb-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-1 py-2 text-left"
                  onClick={() => setSubCategoryOpen((v) => !v)}
                >
                  <span className="text-[14px] font-medium text-[#24303A]">Sub Category</span>
                  <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
                </button>
                {subCategoryOpen && (
                  <div className="space-y-2 px-2 pb-2 text-[14px] text-[#6E7B86]">
                    {subcategoryOptions.length === 0 && (
                      <div className="text-[12px] text-slate-400">No sub categories</div>
                    )}
                    {subcategoryOptions.map((sc) => (
                      <label key={sc} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.has(sc)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPage(1);
                            setSelectedSubcategories((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(sc);
                              else next.delete(sc);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 accent-[#7352C7]"
                          style={{ accentColor: "#7352C7" }}
                        />
                        <span>{sc}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 border-t border-[#E6EAF0] bg-white p-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setSelectedBrands(new Set());
                    setSelectedCategories(new Set());
                    setSelectedSubcategories(new Set());
                  }}
                  className="w-1/2 rounded-full border border-[#E6EAF0] bg-white px-3 py-2 text-[14px] font-semibold text-[#24303A]"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-1/2 rounded-full bg-[#7352C7] px-3 py-2 text-[14px] font-semibold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

